import React from "react";
import {Area, SeatMap, Zone} from "../../types/event";
import {useZoomPan} from "../../hooks/useZoomPan";
import {useSeatMapData} from "../../hooks/useSeatMapData";
import {seatKey} from "../../lib/seats";
import RowLabels from "./RowLabels";

/**
 * Read-only render of a seat map for library/preview contexts (admin library,
 * organiser picker). Reuses the parsing/zoom hooks but draws seats statically:
 * no reservation, selection or table-group interaction. The interactive checkout
 * renderer lives in features/event-details/SeatMapSection.tsx and is left untouched.
 *
 * `decorateSeat` and `onSeatClick` let a caller repaint and click seats without a
 * third copy of this SVG walk. The administrator seating screen uses them to paint
 * occupancy and pick a seat to move a ticket to; passing neither leaves the sold and
 * reserved rendering exactly as it was.
 */

const STATUS_COLORS = {
    reserved: "#FBBF24",
    sold: "#6B7280",
};

/**
 * How one seat should be drawn, overriding the default sold/reserved painting. `emphasis`
 * rings the seat, for the one the operator is acting on. `title` becomes the SVG tooltip.
 */
export type SeatDecoration = {
    fill?: string;
    stroke?: string;
    /** The seat number is drawn white; set this for a dark fill of your own. */
    lightText?: boolean;
    emphasis?: boolean;
    title?: string;
};

export type SeatDecorator = (seatGuid: string, category: string) => SeatDecoration | null;

const deg = (n?: number) => (typeof n === "number" ? n : 0);

type Point = { x: number; y: number };

function polygonBBox(points: Point[]) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    }
    return {cx: (minX + maxX) / 2, cy: (minY + maxY) / 2};
}

function toPointsAttr(points: Point[]) {
    return points.map((p) => `${p.x},${p.y}`).join(" ");
}

function AreaShape({area, zone}: { area: Area; zone: Zone }) {
    const posX = (area.position?.x ?? 0) + (zone.position?.x ?? 0);
    const posY = (area.position?.y ?? 0) + (zone.position?.y ?? 0);
    const rotation = deg(area.rotation);
    const fill = area.color || "#E5E7EB";
    const stroke = area.border_color || "#9CA3AF";

    if (area.shape === "rectangle" && area.rectangle) {
        const w = area.rectangle.width;
        const h = area.rectangle.height;
        const cx = w / 2;
        const cy = h / 2;
        return (
            <g transform={`translate(${posX}, ${posY}) rotate(${rotation}, ${cx}, ${cy})`}>
                <rect width={w} height={h} fill={fill} stroke={stroke} strokeWidth={2} rx={4} ry={4}/>
                {area.text?.text && (
                    <text
                        x={area.text.position?.x ?? cx}
                        y={area.text.position?.y ?? cy}
                        fontSize={area.text.size ?? 20}
                        fill={area.text.color || "#374151"}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{pointerEvents: "none", userSelect: "none"}}
                    >
                        {area.text.text}
                    </text>
                )}
            </g>
        );
    }

    if (area.shape === "polygon" && area.polygon?.points?.length) {
        const pts = area.polygon.points as Point[];
        const bbox = polygonBBox(pts);
        return (
            <g transform={`translate(${posX}, ${posY}) rotate(${rotation}, ${bbox.cx}, ${bbox.cy})`}>
                <polygon points={toPointsAttr(pts)} fill={fill} stroke={stroke} strokeWidth={2}
                         vectorEffect="non-scaling-stroke"/>
                {area.text?.text ? (
                    <text
                        x={area.text.position?.x ?? bbox.cx}
                        y={area.text.position?.y ?? bbox.cy}
                        fontSize={area.text.size ?? 20}
                        fill={area.text.color || "#374151"}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{pointerEvents: "none", userSelect: "none"}}
                    >
                        {area.text.text}
                    </text>
                ) : null}
            </g>
        );
    }

    if (area.shape === "text" && area.text) {
        const tx = posX + (area.text.position?.x ?? 0);
        const ty = posY + (area.text.position?.y ?? 0);
        return (
            <g transform={`translate(${tx}, ${ty}) rotate(${rotation})`}>
                <text
                    x={0}
                    y={0}
                    fontSize={area.text.size ?? 24}
                    fill={area.text.color || "#374151"}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{pointerEvents: "none", userSelect: "none"}}
                >
                    {area.text.text}
                </text>
            </g>
        );
    }

    return null;
}

function StaticSeat({
                        seat,
                        zone,
                        row,
                        soldSet,
                        reservedSet,
                        categoryColor,
                        decorateSeat,
                        onSeatClick,
                    }: {
    seat: any;
    zone: Zone;
    row: { position?: { x: number; y: number } };
    soldSet: Set<string>;
    reservedSet: Set<string>;
    categoryColor: Record<string, string>;
    decorateSeat?: SeatDecorator;
    onSeatClick?: (seatGuid: string, category: string) => void;
}) {
    const seatX = (zone.position?.x ?? 0) + (row.position?.x ?? 0) + (seat.position?.x ?? 0);
    const seatY = (zone.position?.y ?? 0) + (row.position?.y ?? 0) + (seat.position?.y ?? 0);

    // Keyed case-insensitively: both sets come from the server, which stores a seat in the
    // layout's own spelling and normalises what clients send to it.
    const isSold = soldSet.has(seatKey(seat.seat_guid));
    const isReserved = reservedSet.has(seatKey(seat.seat_guid));

    let fillColor = categoryColor[seat.category] || "#D1D5DB";
    let strokeColor = "#4B5563";
    if (isSold) {
        fillColor = STATUS_COLORS.sold;
        strokeColor = "#111827";
    } else if (isReserved) {
        fillColor = STATUS_COLORS.reserved;
        strokeColor = "#B45309";
    }

    // A decorator wins over the sold/reserved default, because it is answering a question this
    // component cannot: on the seating screen a seat is sold, unpaid, double booked or free,
    // and takenSeats collapses the middle two into one colour.
    const decoration = decorateSeat?.(seat.seat_guid, seat.category) ?? null;
    if (decoration?.fill) fillColor = decoration.fill;
    if (decoration?.stroke) strokeColor = decoration.stroke;

    const darkFill = isSold || !!decoration?.lightText;
    const clickable = !!onSeatClick;

    return (
        <g
            transform={`translate(${seatX}, ${seatY})`}
            onClick={clickable ? () => onSeatClick!(seat.seat_guid, seat.category) : undefined}
            style={clickable ? {cursor: "pointer"} : undefined}
        >
            {decoration?.emphasis && (
                <circle
                    r={(seat.radius ?? 12) + 5}
                    fill="none"
                    stroke={decoration.stroke || strokeColor}
                    strokeWidth={2}
                    strokeDasharray="3 2"
                />
            )}
            <circle
                r={seat.radius ?? 12}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={decoration?.emphasis ? 3 : 1.5}
            />
            <text
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={seat.radius ? seat.radius * 0.9 : 10}
                fill={darkFill ? "#FFFFFF" : "#000000"}
                pointerEvents="none"
                style={{userSelect: "none"}}
            >
                {seat.seat_number}
            </text>
            {decoration?.title && <title>{decoration.title}</title>}
        </g>
    );
}

export default function SeatMapPreview({
                                           seatMap,
                                           decorateSeat,
                                           onSeatClick,
                                           legend,
                                       }: {
    seatMap: SeatMap;
    decorateSeat?: SeatDecorator;
    onSeatClick?: (seatGuid: string, category: string) => void;
    /** Replaces the category and sold/reserved legend, which says the wrong thing once a
     *  decorator has repainted the map. */
    legend?: React.ReactNode;
}) {
    const {
        handleWheel,
        zoomIn,
        zoomOut,
        resetView,
        handleMouseDown,
        handleMouseUp,
        handleMouseLeave,
        handleMouseMove,
        transformStyle,
    } = useZoomPan();

    const {parsedMap, width, height, categoryColor, soldSet, reservedSet} = useSeatMapData(seatMap, []);

    return (
        <div className="space-y-3">
            {/* Legend */}
            {legend ?? (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    {parsedMap?.categories?.map((cat) => (
                        <div key={cat.name}
                             className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2 py-1 shadow-sm">
                            <span className="inline-block h-3 w-3 rounded-sm border border-slate-400"
                                  style={{backgroundColor: cat.color}}/>
                            <span className="text-slate-600">{cat.name}</span>
                        </div>
                    ))}
                    {parsedMap?.categories?.length ?
                        <span className="hidden h-4 w-px bg-slate-200 sm:inline-block"/> : null}
                    <div
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2 py-1 shadow-sm">
                        <span className="inline-block h-3 w-3 rounded-sm border border-slate-400"
                              style={{backgroundColor: STATUS_COLORS.reserved}}/>
                        <span className="text-slate-600">Reserved</span>
                    </div>
                    <div
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2 py-1 shadow-sm">
                        <span className="inline-block h-3 w-3 rounded-sm border border-slate-400"
                              style={{backgroundColor: STATUS_COLORS.sold}}/>
                        <span className="text-slate-600">Sold</span>
                    </div>
                </div>
            )}

            <div
                className="relative w-full overflow-auto rounded-xl border border-slate-200 bg-slate-50"
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
            >
                <div
                    className="pointer-events-auto absolute right-3 top-3 z-10 flex flex-col gap-1 rounded-md bg-white/80 p-1 text-xs shadow">
                    <button type="button" className="rounded px-2 py-1 hover:bg-slate-100" onClick={zoomIn}>+</button>
                    <button type="button" className="rounded px-2 py-1 hover:bg-slate-100" onClick={zoomOut}>−</button>
                    <button type="button" className="mt-1 rounded px-2 py-1 text-[10px] hover:bg-slate-100"
                            onClick={resetView}>reset
                    </button>
                </div>

                <div className="relative" style={{width: "100%", paddingBottom: `${(height / width) * 100}%`}}>
                    {!parsedMap ? (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500">
                            Unable to load seat map.
                        </div>
                    ) : (
                        <svg className="absolute inset-0 h-full w-full" viewBox={`0 0 ${width} ${height}`}
                             style={{userSelect: "none"}}>
                            <g transform={transformStyle(width, height)}>
                                {parsedMap.zones?.flatMap((zone) =>
                                    (zone.areas || []).map((area) => (
                                        <AreaShape key={`area-${area.uuid}`} area={area} zone={zone}/>
                                    ))
                                )}

                                {/* Above the areas so a shape covering the block cannot paint over
                                    the letters, below the seats so it never sits on one. */}
                                <RowLabels zones={parsedMap.zones ?? []}/>

                                {parsedMap.zones?.flatMap((zone) =>
                                    (zone.rows || []).flatMap((row) =>
                                        row.seats.map((seat: any) => (
                                            <StaticSeat
                                                key={seat.uuid}
                                                seat={seat}
                                                zone={zone}
                                                row={row}
                                                soldSet={soldSet}
                                                reservedSet={reservedSet}
                                                categoryColor={categoryColor}
                                                decorateSeat={decorateSeat}
                                                onSeatClick={onSeatClick}
                                            />
                                        ))
                                    )
                                )}
                            </g>
                        </svg>
                    )}
                </div>
            </div>
        </div>
    );
}
